import { store } from '@redux/store';
import { fromEvent, shareReplay, take } from 'rxjs';
import ModalStep from '../enums/modalStep';
import { scheduleTour, setModalStep } from '../redux/slices';
import Email from './email';
import UserStatus from '../enums/userStatus';
import { getVerificationMethodStep } from '../services/verificationMethodService';
import { getStoredUserData } from '../services/userService';

export default class SignIn {
  constructor(rootComponent) {
    this.subscriptions = [];
    this.rootComponent = rootComponent;
    this.panelSelector = '[data-sign-in-panel]';
    this.previousStep = ModalStep.SignUp;
    this.email = new Email();
    this.skipQuestions = false;
    this.rootComponent.subscribe('domUpdate', this.onDomUpdate.bind(this));
  }

  /**
   * Binds event listeners to the necessary DOM elements.
   * Specifically, it listens for a click event on the sign-up link
   * and triggers the `moveToSignUpPanel` method when the event occurs.
   */
  bindEvents() {
    const signUpLink = this.getSignUpLink();
    const onSignUpLinkClick$ = fromEvent(signUpLink, 'click');
    onSignUpLinkClick$.subscribe(this.moveToSignUpPanel.bind(this));
  }

  /**
   * Navigates to the sign-up panel by dispatching an action to update the modal step.
   * The modal step is set to the previous step stored in the component's state.
   */
  moveToSignUpPanel() {
    store.dispatch(setModalStep({ modalStep: this.previousStep }));
  }

  /**
   * Handles the DOM update event for the sign-in component.
   * Subscribes to the email change event, disables the primary button,
   * and binds necessary events if the current step is the SignIn step.
   *
   * @param {CustomEvent} event - The DOM update event containing details about the current step.
   * @param {Object} event.detail - The detail object of the event.
   * @param {string} event.detail.step - The current step in the modal process.
   */
  onDomUpdate(event) {
    if (event.detail && event.detail.step !== ModalStep.SignIn) {
      return;
    }
    this.rootComponent.setPrimaryButtonDisabled(true);
    this.email.getOnChangeEvent$().subscribe(this.onEmailChange.bind(this));
    if (this.email.getNode().value) {
      this.rootComponent.setPrimaryButtonDisabled(false);
    }
    this.rootComponent.setSecondaryButtonDisabled(false);
    if (Object.values(this.rootComponent.tourQuestions).length === 0) {
      this.skipQuestions = true;
    }
  this.bindEvents();
  }

  /**
   * Handles the email input change event.
   * Validates the email input and enables or disables the primary button
   * based on the validation result.
   */
  onEmailChange() {
    const isValid = this.email.validate();
    if (isValid) {
      this.rootComponent.setPrimaryButtonDisabled(false);
      return;
    }
    this.rootComponent.setPrimaryButtonDisabled(true);
  }

  showErrorMessage(message) {
    this.rootComponent.showNotification(message);
  }

  /**
   * Handles the success response of the email verification process.
   *
   * @param {Object} data - The response data from the email verification process.
   * @param {Object} data.status - The status object containing the verification status.
   * @param {string} data.status.key - The key representing the user's verification status.
   *
   */
  onVerifyEmailSuccess(data) {
    this.rootComponent.setPrimaryButtonDisabled(false);
    this.rootComponent.removeNotification();
    const { status, error } = data;

    if (error) {
      this.showErrorMessage('An error has occurred. Please try again.');
      return;
    }

    switch (UserStatus.fromKey(status.name)) {
      case UserStatus.Verified:
        if (this.skipQuestions) {
          // Disable the primary button to prevent multiple clicks
          this.rootComponent.setPrimaryButtonDisabled(true);
          store.dispatch(scheduleTour());
        } else {
          store.dispatch(setModalStep({ modalStep: ModalStep.Questions }));
        }
        break;
      case UserStatus.NotVerified: {
        const step = getVerificationMethodStep(this.rootComponent.verificationMethods);
        store.dispatch(setModalStep({ modalStep: step }));
        break;
      }
      case UserStatus.NotFound:
        this.showErrorMessage('A UTour account does not exist for this email.');
        break;
      case UserStatus.UserBlocked:
        this.showErrorMessage('User is blocked. Please contact support.');
        break;
      default:
        this.rootComponent.setPrimaryButtonDisabled(false);
        this.showErrorMessage('An error has occurred. Please try again.');
        break;
    }
  }

  /**
   * Updates the user data by notifying changes in user status and user information.
   *
   * @param {Object} param - The parameter object.
   * @param {string} [param.status] - The new status of the user. If provided, it triggers a status change event.
   * @param {Object} [param.user] - The user object containing updated user information.
   * If provided, it triggers a user change event.
   */
  setUserData({ status, user }) {
    this.rootComponent.onUserStatusChange$.next(status);
    if (user) {
      this.rootComponent.onUserChange$.next(user);
      return;
    }

    this.rootComponent.onUserChange$.next({
      email: this.email.getValue(),
    });
  }

  /**
   * Handles the primary action for the component.
   * Disables the primary button and initiates the email verification process.
   */
  handlePrimaryAction() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.rootComponent.setPrimaryButtonDisabled(true);
    const onVerifyEmail$ = this.rootComponent.verifyUserEmail$(this.email.getValue()).pipe(take(1), shareReplay(1));
    this.subscriptions.push(onVerifyEmail$.subscribe(this.setUserData.bind(this)));
    this.subscriptions.push(onVerifyEmail$.subscribe(this.onVerifyEmailSuccess.bind(this)));
  }

  /**
   * Handles the secondary action by dispatching an action to update the modal step
   * to the previous step in the application's state.
   */
  handleSecondaryAction() {
    this.moveToSignUpPanel();
  }

  /**
   * Retrieves the sign-in panel element from the DOM.
   *
   * @returns {Element} The DOM element matching the panel selector, or null if no match is found.
   */
  getSignInPanel() {
    return document.querySelector(this.panelSelector);
  }

  /**
   * Retrieves the sign-up link element from the sign-in panel.
   *
   * @returns {Element} The DOM element representing the sign-up link, or null if not found.
   */
  getSignUpLink() {
    return this.getSignInPanel().querySelector('[data-sign-up-link]');
  }

  /**
   * Renders the Sign In component as an HTML string.
   *
   * @returns {string} The HTML string representing the Sign In component.
   *
   * @description
   * This method generates the HTML structure for a Sign In panel, including:
   * - A heading for signing in to UTour.
   * - A prompt to sign in to continue scheduling a tour.
   * - An email input field rendered via `this.email.getHtml()`.
   * - A link to sign up for an account if the user doesn't have one.
   * - A disclaimer about agreeing to UTour's Privacy & Terms.
   *
   * The component uses various CSS classes for styling and layout, and includes
   * data attributes for interactivity (e.g., `data-sign-in-panel`, `data-sign-up-link`).
   */
  render() {
    const { email } = getStoredUserData();
    this.email.value = email;
    return `
      <div class="nhs-c-self-guided-left-panel" data-sign-in-panel>
        <div>
          <h1 class="nhs-c-heading--self-guided-tour nhs-mq-heading--self-guided-tour-large">Sign in to UTour</h1>

          <div class="nhs-c-notification__placeholder" data-self-guided-tour-notifications></div>

          <p class="nhs-c-label--black-900-body-1-medium-regular">Sign in to your account to continue scheduling your tour.</p>
        </div>

        <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600">
          ${this.email.getHtml()}

          <p class="nhs-c-label--black-900-body-1-medium-regular">Don't have an account? 
            <span data-sign-up-link class="nhs-c-label--black-1000-body-medium-regular-underline nhs-u-cursor--pointer">Sign up</span>
          </p>

          <p class="nhs-c-label--black-800-sans-label-1-extra-extra-extra-small-book">By clicking Sign in, I agree to 
            <a href="https://www.utourhomes.com/privacy-terms" target="_blank" class="nhs-u-text-underline nhs-u-color--black-1000 nhs-u-cursor--pointer">
              UTour's Privacy & Terms.
            </a>
          </p>
        </div>
      </div>
    `;
  }
}
